// test comment for ts_test
import * as grpcWeb from "grpc-web";
import { v4 as uuid } from "uuid";

import { LanAgentClient } from "@formant/core/client/LanAgentClient";
import { browser } from "@formant/core/common/browser";
import { collectErrors } from "@formant/core/common/collectErrors";
import { defined } from "@formant/core/common/defined";
import { delay } from "@formant/core/common/delay";
import { filterIceServers } from "@formant/core/common/filterIceServers";
import { logger } from "@formant/core/common/logger";
import { redactStringifiedFields } from "@formant/core/common/redact";
import {
    IPromiseIntervalHandle,
    setPromiseInterval,
} from "@formant/core/common/setPromiseInterval";
import { stats } from "@formant/core/common/stats";
import { duration } from "@formant/core/model/duration";
import { IRtcIceServer } from "@formant/core/model/IRtcIceServer";
import { IRtcInfo } from "@formant/core/model/IRtcInfo";
import { IRtcPeer } from "@formant/core/model/IRtcPeer";
import { RtcConnectionStatus } from "@formant/core/model/RtcConnectionStatus";
import { Timestamp } from "@formant/core/model/Timestamp";
import { Uuid } from "@formant/core/model/Uuid";
import {
    CreatePeerRequest,
    DeletePeerRequest,
    GetIceServersRequest,
    GetPeersRequest,
    Peer,
    ReceiveSignalStreamRequest,
    ReceiveSignalStreamResponse,
    RefreshPeerRequest,
    SendSignalRequest,
} from "@formant/protos";

import { DeadlineEnforcer } from "./DeadlineEnforcer";
import { IPingInfo } from "./models/IPingInfo";
import { IRtcClientConfiguration } from "./models/IRtcClientConfiguration";
import { IRtcConnectionStatsInfo } from "./models/IRtcConnectionStatsInfo";
import { IRtcSendConfiguration } from "./models/IRtcSendConfiguration";
import { IRtcSessionMetricsMessageCounts } from "./models/IRtcSessionMetricsMessageCounts";
import { IRtcStreamMessage } from "./models/IRtcStreamMessage";
import { IStreamControl } from "./models/IStreamControl";
import { RtcConnection } from "./RtcConnection";
import { forceGarbageCollection } from "./utils/garbageCollect";
import { getErrorMessage } from "./utils/getErrorMessage";
import { iceModeFromRtcConnectionStatsInfo } from "./utils/iceModeFromRtcConnectionInfo";
import { partition } from "./utils/partition";
import { peerToRtcPeer } from "./utils/peerToRtcPeer";

const redactRtcIceServers = (servers: IRtcIceServer[]) =>
    servers.map(_ => ({
        ..._,
        ...(_.username ? { username: "<REDACTED>" } : null),
        ...(_.credential ? { credential: "<REDACTED>" } : null),
    }));

/**
 * RtcClient to be used by browsers.
 */
export class RtcClient {
    private readonly grpcCallDeadline = 5 * duration.second;
    private readonly refreshIntervalDelayMs = 2 * duration.second;
    private readonly iceServersTtl = 30 * duration.minute;
    private localPeer?: Peer;
    private iceServers?: IRtcIceServer[];
    private iceServersLastUpdate?: Timestamp;
    private receiveSignalStream?: grpcWeb.ClientReadableStream<ReceiveSignalStreamResponse>;
    private refreshInterval: IPromiseIntervalHandle;
    private connections: RtcConnection[] = [];
    private localConnections: RtcConnection[] = [];
    private isConnectionInitiator: WeakSet<RtcConnection> = new WeakSet();
    private isOutgoingConnection: Set<Uuid> = new Set();
    private peerDataChannelListeners: ((
        remotePeerId: Uuid,
        channel: RTCDataChannel,
    ) => void)[] = [];
    private rtcInstancesConstructed = 0;
    private statsLoopsExecuted = 0;

    constructor(private config: IRtcClientConfiguration) {
        const { refreshIntervalDelayMs } = this;
        this.refreshInterval = setPromiseInterval({
            func: async () => {
                try {
                    await this.update();
                } catch (error) {
                    logger.warn("RTC refresh failed", { error });
                }
            },
            delay: refreshIntervalDelayMs,
        });
    }

    public send(
        remotePeerId: Uuid,
        message: IRtcStreamMessage,
        config: IRtcSendConfiguration,
    ) {
        const connection = this.getActiveConnection(remotePeerId);
        if (!connection) {
            logger.warn("Send called with no connection.");
            return;
        }
        if (!connection.isReady()) {
            logger.warn("Send called with unready connection.");
            return;
        }
        connection.send(message, config);
    }

    public controlRemoteStream(
        remotePeerId: Uuid,
        streamControl: IStreamControl,
    ) {
        const connection = this.getActiveConnection(remotePeerId);
        if (!connection) {
            logger.warn("controlRemoteStream called with no connection.");
            return;
        }
        if (!connection.isReady()) {
            logger.warn("controlRemoteStream called with unready connection.");
            return;
        }
        connection.controlRemoteStream(streamControl);
    }

    public async getLocalPeer() {
        while (!this.localPeer) {
            await delay(0.1 * duration.second); // TODO: this is for backwards compat ... is there a better way?
        }
        return peerToRtcPeer(this.localPeer);
    }

    /**
     * Connects to a peer given its id.
     * Returns the id of the session, if one is created.
     */
    public async connect(
        remotePeerId: Uuid,
        rtcInfo?: IRtcInfo,
        options?: { lockDevice?: boolean },
    ): Promise<Uuid | undefined> {
        if (this.config.lanOnlyMode === true) {
            // LAN-only RTC client should only use the connectLan method.
            throw new Error("connect method called in local only mode.");
        }

        const { track, signalingClient } = this.config;

        const { localPeer, receiveSignalStream, iceServers } = this;
        if (!localPeer || !receiveSignalStream || !iceServers) {
            logger.warn(
                "Connect called prior to local peer, receiveSignalStream, and ICE servers ready",
            );
            return;
        }

        // If there's already an active connection, do nothing and return.
        if (this.getActiveConnection(remotePeerId)) {
            logger.warn("Connect called for peer with existing connection.");
            return;
        }
        if (this.isOutgoingConnection.has(remotePeerId)) {
            logger.warn(
                "Connect called for peer with an existing outgoing connection offer.",
            );
            return;
        }

        this.isOutgoingConnection.add(remotePeerId);

        const resolvedLockDevice =
            options?.lockDevice ?? this.config.lockDevice;

        const filteredIceServers = filterIceServers(iceServers, rtcInfo);

        logger.debug("Received ICE servers:", redactRtcIceServers(iceServers));
        logger.debug(
            "Using ICE servers:",
            redactRtcIceServers(filteredIceServers),
        );

        // Create a new remote connection.
        const connection = new RtcConnection(
            await this.createRTCPeerConnection(filteredIceServers),
            filteredIceServers,
            {
                baseConfig: {
                    isOffer: true, // A user connecting to a device is always the offer peer right now.
                    isLan: false,
                    receive: (peerId: Uuid, message: IRtcStreamMessage) =>
                        this.config.receive(peerId, message),
                    onStreamsInfoUpdate: this.config.onStreamsInfoUpdate
                        ? (peerId: Uuid, timestamp: Timestamp) =>
                              this.config.onStreamsInfoUpdate?.(
                                  peerId,
                                  timestamp,
                              )
                        : undefined,
                    track: this.config.lanOnlyMode
                        ? undefined
                        : this.config.track,
                    localUserId: localPeer.getUserId()?.getValue(),
                    localPeerId: localPeer.getPeerId(),
                    remotePeerId,
                    sessionType: this.config.sessionType,
                    lockDevice: resolvedLockDevice,
                },
                remoteConfig: {},
            },
            channel => this.onCustomDataChannel(remotePeerId, channel),
        );

        // We don't know the session ID or timestamp yet because the
        // signaling API is the source of truth.
        // Once we know, we will set them with `connection.set`

        const offerSignal = await connection.getOffer();
        if (!offerSignal) {
            logger.error("Failed to generate offer.");
            this.isOutgoingConnection.delete(remotePeerId);
            return;
        }

        logger.debug("Sending offer.");

        track?.("Sending offer", redactStringifiedFields(offerSignal));

        if (resolvedLockDevice) {
            offerSignal.setLockDevice(true);
        }
        const sendSignalRequest = new SendSignalRequest();
        sendSignalRequest.setSignal(offerSignal);

        const response = await (async () => {
            try {
                return await DeadlineEnforcer.withDeadline(
                    signalingClient.sendSignal(
                        sendSignalRequest,
                        await this.getMetadata(),
                    ),
                    this.grpcCallDeadline,
                );
            } catch (error) {
                logger.warn("Error when sending signal", { error });
                this.config.onConnectionError?.(error);
            }
        })();

        if (!this.isOutgoingConnection.has(remotePeerId)) {
            logger.debug("No offer set after receiving offer signal response.");
            return; // Reset was called while the offer was on its way.
        }

        logger.debug("Offer sent.");

        const sessionId = response?.getSessionId()?.getValue();
        const sessionCreatedTimestamp = response
            ?.getSessionCreatedTimestamp()
            ?.getValue();
        if (!sessionId || !sessionCreatedTimestamp) {
            logger.warn(
                "No session ID or no session created timestamp on send signal response.",
            );
            this.isOutgoingConnection.delete(remotePeerId);
            return;
        }
        connection.setSessionId(sessionId);
        connection.setSessionCreatedTimestamp(sessionCreatedTimestamp);

        // Add the new connection to the connections list
        this.isOutgoingConnection.delete(remotePeerId);
        this.connections.push(connection);
        this.isConnectionInitiator.add(connection);
        this.setupHandlers(connection);
        return sessionId;
    }

    // Connect to a Formant agent directly using its IP address and port.
    public async connectLan(agentEndpoint: string): Promise<Uuid | false> {
        const lanAgentClient = new LanAgentClient(agentEndpoint);

        const connection = new RtcConnection(
            await this.createRTCPeerConnection([]),
            [],
            {
                baseConfig: {
                    isOffer: true,
                    isLan: true,
                    receive: (peerId: Uuid, message: IRtcStreamMessage) =>
                        this.config.receive(peerId, message),
                    onStreamsInfoUpdate: this.config.onStreamsInfoUpdate
                        ? (peerId: Uuid, timestamp: Timestamp) =>
                              this.config.onStreamsInfoUpdate?.(
                                  peerId,
                                  timestamp,
                              )
                        : undefined,
                    track: this.config.lanOnlyMode
                        ? undefined
                        : this.config.track,
                    localUserId: this.localPeer?.getUserId()?.getValue(),
                    localPeerId: "lan_client",
                    remotePeerId: agentEndpoint,
                },
            },
            channel => this.onCustomDataChannel(agentEndpoint, channel),
        );

        const description = await connection.getLanOffer();
        if (!description) {
            logger.warn("Could not generate LAN offer");
            return false;
        }

        // Make the agent API call here
        const response = await lanAgentClient.postLanRtcOffer({
            offer: JSON.stringify(description),
        });

        const answerDescription = new RTCSessionDescription(
            JSON.parse(response.answer),
        );
        await connection.handleLanAnswer(answerDescription);

        this.localConnections.push(connection);
        this.isConnectionInitiator.add(connection);
        return uuid();
    }

    public getConnections(): RtcConnection[] {
        return [...this.connections, ...this.localConnections];
    }

    public createCustomDataChannel(
        remotePeerId: Uuid,
        channelName: string,
        init: RTCDataChannelInit,
        isBinary: boolean,
        listener: (peerId: Uuid, channel: RTCDataChannel) => void,
    ): () => void {
        const customChannelPrefix = "custom.";
        const connection = this.getActiveConnection(remotePeerId);
        if (connection && this.isConnectionInitiator.has(connection)) {
            const channel = connection.peerConnection.createDataChannel(
                customChannelPrefix + channelName,
                init,
            );
            if (isBinary) {
                channel.binaryType = "arraybuffer";
            }
            listener(remotePeerId, channel);
            return () => {
                return;
            };
        }
        const processor = (peerId: Uuid, channel: RTCDataChannel) => {
            if (
                remotePeerId === peerId &&
                channel.label === customChannelPrefix + channelName
            ) {
                listener(remotePeerId, channel);
            }
        };
        this.peerDataChannelListeners.push(processor);
        return () => {
            this.peerDataChannelListeners =
                this.peerDataChannelListeners.filter(_ => _ !== processor);
        };
    }

    private onCustomDataChannel(remotePeerId: Uuid, channel: RTCDataChannel) {
        this.peerDataChannelListeners.forEach(_ => _(remotePeerId, channel));
    }

    public getConnectionStatus(remotePeerId: Uuid): RtcConnectionStatus {
        if (this.isOutgoingConnection.has(remotePeerId)) {
            return "connecting";
        }
        const connection = this.getActiveConnection(remotePeerId);
        if (!connection) {
            return "disconnected";
        }
        return connection.isReady() ? "connected" : "connecting";
    }

    public async getConnectionStatsInfo(
        remotePeerId: Uuid,
    ): Promise<IRtcConnectionStatsInfo | undefined> {
        const connection = this.getActiveConnection(remotePeerId);
        if (!connection) {
            return;
        }
        return await connection.getConnectionStatsInfo();
    }

    public async disconnect(remotePeerId: Uuid) {
        const connection = this.getActiveConnection(remotePeerId);
        if (connection) {
            await connection.close();
            this.connections = this.connections.filter(it => it !== connection);
            this.localConnections = this.localConnections.filter(
                it => it !== connection,
            );
        }
    }

    public async getPeers(): Promise<IRtcPeer[]> {
        if (this.config.lanOnlyMode) {
            throw new Error(
                "getPeers method cannot be used in local-only mode.",
            );
        }

        const { signalingClient } = this.config;

        const response = await (async () => {
            try {
                return await DeadlineEnforcer.withDeadline(
                    signalingClient.getPeers(
                        new GetPeersRequest(),
                        await this.getMetadata(),
                    ),
                    this.grpcCallDeadline,
                );
            } catch (error) {
                logger.warn("Error when getting peers", { error });
            }
        })();

        if (!response) {
            return [];
        }

        const peers = response.getPeersList();
        if (!peers) {
            return [];
        }

        const score = (_: Peer) => _.getPeerCreatedTimestamp()?.getValue() ?? 0;
        const sortedPeers = peers.sort((a, b) => score(b) - score(a));

        return sortedPeers.map(_ => peerToRtcPeer(_));
    }

    public async getSessions(): Promise<{ [key in string]: string[] }> {
        if (this.config.lanOnlyMode) {
            throw new Error(
                "getPeers method cannot be used in local-only mode.",
            );
        }

        const { signalingClient } = this.config;

        const response = await (async () => {
            try {
                return await DeadlineEnforcer.withDeadline(
                    signalingClient.getPeers(
                        new GetPeersRequest(),
                        await this.getMetadata(),
                    ),
                    this.grpcCallDeadline,
                );
            } catch (error) {
                logger.warn("Error when getting peers", { error });
            }
        })();

        if (!response) {
            return {};
        }

        const peers = response.getPeersList();
        const peerSessions: { [key in string]: string[] } = {};
        for (const peer of peers) {
            peerSessions[peer.getPeerId()] = peer.getSessionIdsList();
        }
        return peerSessions;
    }

    public getPing(remotePeerId: Uuid): number | undefined {
        const connection = this.getActiveConnection(remotePeerId);
        if (!connection) {
            logger.warn("Attempted to get ping time from inactive peer.");
            return;
        }
        return connection.getPing();
    }

    public getPingInfo(remotePeerId: Uuid): IPingInfo | undefined {
        const connection = this.getActiveConnection(remotePeerId);
        if (!connection) {
            logger.warn("Attempted to get ping time from inactive peer.");
            return;
        }
        return connection.getPingInfo();
    }

    public getLastMessageTimestamp(remotePeerId: Uuid): Timestamp | undefined {
        const connection = this.getActiveConnection(remotePeerId);
        if (!connection) {
            logger.warn(
                "Attempted to get last message time from inactive peer.",
            );
            return;
        }
        return connection.getLastMessageTimestamp();
    }

    public getSessionMetricsMessageCounts(
        remotePeerId: Uuid,
    ): IRtcSessionMetricsMessageCounts | undefined {
        const connection = this.getActiveConnection(remotePeerId);
        if (!connection) {
            logger.warn(
                "Attempted to get session metrics counts from inactive peer.",
            );
            return;
        }
        return connection.getSessionMetricsMessageCounts();
    }

    // Returns true if this RtcClient is ready to make connections and false otherwise
    public isReady(): boolean {
        if (this.config.lanOnlyMode) {
            return true;
        }
        const { localPeer, receiveSignalStream, iceServers } = this;
        if (localPeer && receiveSignalStream && iceServers) {
            return true;
        }
        return false;
    }

    public async shutdown() {
        await this.refreshInterval.stop();
        if (this.receiveSignalStream) {
            this.receiveSignalStream.cancel();
        }
        const connections = this.connections;
        this.connections = [];
        await this.closeConnections(connections);

        const localConnections = this.localConnections;
        this.localConnections = [];
        await this.closeConnections(localConnections);

        this.isOutgoingConnection.clear();
        this.peerDataChannelListeners = [];

        const { localPeer } = this;
        if (!localPeer) {
            return;
        }

        if (this.config.lanOnlyMode) {
            return;
        }

        const { signalingClient } = this.config;

        await (async () => {
            try {
                const request = new DeletePeerRequest();
                request.setPeerId(localPeer.getPeerId());
                await DeadlineEnforcer.withDeadline(
                    signalingClient.deletePeer(
                        request,
                        await this.getMetadata(),
                    ),
                    this.grpcCallDeadline,
                );
            } catch (error) {
                logger.warn("Error deleting local peer", { error });
                return;
            }
        })();
    }

    private async createPeer() {
        if (this.config.lanOnlyMode) {
            throw new Error(
                "createPeer method cannot be used in local-only mode.",
            );
        }

        const { signalingClient } = this.config;

        const createPeerResponse = await (async () => {
            try {
                return await DeadlineEnforcer.withDeadline(
                    signalingClient.createPeer(
                        new CreatePeerRequest(),
                        await this.getMetadata(),
                    ),
                    this.grpcCallDeadline,
                );
            } catch (err) {
                const error = err as unknown;
                logger.debug("createPeer failed", { error });
                const message = getErrorMessage(error);
                throw new Error(`Was not able to create peer: ${message}`);
            }
        })();

        const peer = createPeerResponse?.getPeer();
        if (!peer) {
            throw new Error("Response did not provide peer.");
        }

        return (this.localPeer = peer);
    }

    private async createReceiveSignalStream() {
        if (this.config.lanOnlyMode) {
            throw new Error(
                "createReceiveSignalStream method cannot be used in local-only mode.",
            );
        }

        const { signalingClient } = this.config;

        const { localPeer, iceServers } = this;
        if (!localPeer || !iceServers) {
            return;
        }

        const { receiveSignalStream } = this;
        if (receiveSignalStream) {
            receiveSignalStream.cancel();
        }

        const receiveSignalStreamRequest = new ReceiveSignalStreamRequest();
        receiveSignalStreamRequest.setPeerId(localPeer.getPeerId());
        const stream = await (async () => {
            try {
                return signalingClient.receiveSignalStream(
                    receiveSignalStreamRequest,
                    await this.getMetadata({ hasDeadline: false }),
                );
            } catch (error) {
                logger.debug("createReceiveSignalStream failed", { error });
                const message = getErrorMessage(error);
                throw new Error(
                    `Unable to create receive signal stream: ${message}`,
                );
            }
        })();

        if (!stream) {
            throw new Error("Response did not provide stream.");
        }

        // Set up handlers for the signal stream.
        stream.on("data", async response => {
            const signal = response.getSignal();
            const sessionId = signal?.getSessionId()?.getValue();
            const senderId = signal?.getSenderId();
            const receiverId = signal?.getReceiverId();
            const sessionCreatedTimestamp = signal
                ?.getSessionCreatedTimestamp()
                ?.getValue();
            if (
                !signal ||
                !sessionId ||
                !senderId ||
                !receiverId ||
                !sessionCreatedTimestamp
            ) {
                logger.warn("Received signal with missing information.");
                return;
            }

            const connection = this.getActiveConnection(senderId);
            if (!connection) {
                // No connection - create one
                logger.debug("Received signal: new connection.");

                // Create a new remote connection.
                const newConnection = new RtcConnection(
                    await this.createRTCPeerConnection(iceServers),
                    iceServers,
                    {
                        baseConfig: {
                            isOffer: false,
                            isLan: false,
                            receive: (
                                peerId: Uuid,
                                message: IRtcStreamMessage,
                            ) => this.config.receive(peerId, message),
                            onStreamsInfoUpdate: this.config.onStreamsInfoUpdate
                                ? (peerId: Uuid, timestamp: Timestamp) =>
                                      this.config.onStreamsInfoUpdate?.(
                                          peerId,
                                          timestamp,
                                      )
                                : undefined,
                            track: this.config.lanOnlyMode
                                ? undefined
                                : this.config.track,
                            localPeerId: localPeer.getPeerId(),
                            localUserId: localPeer.getUserId()?.getValue(),
                            remotePeerId: senderId,
                        },
                        remoteConfig: {
                            sessionId,
                            sessionCreatedTimestamp,
                        },
                    },
                    channel => this.onCustomDataChannel(senderId, channel),
                );
                await newConnection.handleSignal(signal);
                this.connections.push(newConnection);
            } else if (connection.getSessionId() !== sessionId) {
                logger.debug(
                    "Received signal: different session for a peer we're already connected to.",
                );
                // Replace iff the session is more recent.
                if (
                    (connection.getSessionCreatedTimestamp() || 0) >
                    sessionCreatedTimestamp
                ) {
                    return;
                }

                const newConnection = new RtcConnection(
                    await this.createRTCPeerConnection(iceServers),
                    iceServers,
                    {
                        baseConfig: {
                            isOffer: false,
                            isLan: false,
                            receive: (
                                peerId: Uuid,
                                message: IRtcStreamMessage,
                            ) => this.config.receive(peerId, message),
                            onStreamsInfoUpdate: this.config.onStreamsInfoUpdate
                                ? (peerId: Uuid, timestamp: Timestamp) =>
                                      this.config.onStreamsInfoUpdate?.(
                                          peerId,
                                          timestamp,
                                      )
                                : undefined,
                            track: this.config.lanOnlyMode
                                ? undefined
                                : this.config.track,
                            localPeerId: localPeer.getPeerId(),
                            localUserId: localPeer.getUserId()?.getValue(),
                            remotePeerId: senderId,
                        },
                        remoteConfig: {
                            sessionId,
                            sessionCreatedTimestamp,
                        },
                    },
                    channel => this.onCustomDataChannel(senderId, channel),
                );
                await newConnection.handleSignal(signal);
                this.connections.push(newConnection);
            } else {
                // Existing connection
                logger.debug("Received signal: for an existing connection.");
                await connection.handleSignal(signal);
            }
        });
        stream.on("end", () => {
            stream.cancel();
            this.receiveSignalStream = undefined;
        });
        stream.on("error", (error: grpcWeb.RpcError) => {
            switch (error.code) {
                case grpcWeb.StatusCode.CANCELLED:
                case grpcWeb.StatusCode.UNAVAILABLE:
                case grpcWeb.StatusCode.UNKNOWN:
                    logger.debug("Receive signal stream error", { error });
                    break;
                case grpcWeb.StatusCode.UNAUTHENTICATED:
                    logger.warn("Receive signal stream error", { error });
                    break;
                default:
                    logger.error("Receive signal stream error", { error });
            }
            stream.cancel();
            this.receiveSignalStream = undefined;
        });
        return (this.receiveSignalStream = stream);
    }

    private async createRTCPeerConnection(
        iceServers: RTCIceServer[],
    ): Promise<RTCPeerConnection> {
        const RTCPeerConnectionClass =
            this.config.alternateRTCPeerConnection ?? window.RTCPeerConnection;

        if (!RTCPeerConnectionClass) {
            if (browser() !== "Chrome") {
                throw new Error(
                    "WebRTC is not enabled. Please try again with the latest version of Google Chrome.",
                );
            }

            throw new Error(
                "WebRTC is not enabled. Please ensure WebRTC is not disabled by ad blocking software.",
            );
        }

        this.rtcInstancesConstructed++;
        if (!(this.rtcInstancesConstructed % 20)) {
            forceGarbageCollection();
        }

        try {
            return new RTCPeerConnectionClass({
                iceServers,
            });
        } catch (err) {
            // This should be DEBUG, since this will fail often, and we don't want a million entries in SENTRY
            logger.debug(
                `Failed to create RTCPeerConnection: ${this.rtcInstancesConstructed} total allocated`,
            );
            throw err;
        }
    }

    private async closeConnections(connections: RtcConnection[]) {
        const errors = await collectErrors(
            connections.map(async _ => {
                await _.close();
            }),
        );
        for (const error of errors) {
            logger.warn("Request to close session failed", { error });
        }
    }

    private getActiveConnection(remotePeerId: Uuid) {
        return this.getConnections().find(
            _ => _.getRemotePeerId() === remotePeerId && _.isActive(),
        );
    }

    private async update() {
        this.localConnections = this.localConnections.filter(_ => _.isActive());
        if (this.config.lanOnlyMode) {
            return;
        }

        // The rest of this method deals only with remote connections

        const { signalingClient } = this.config;

        // Make sure we have a local peer, are receiving signals, and have ICE servers
        if (!this.localPeer || !this.receiveSignalStream || !this.iceServers) {
            const p: PromiseLike<any>[] = [];
            if (!this.localPeer) {
                p.push(this.createPeer());
            }
            if (!this.iceServers) {
                p.push(this.updateIceServers());
            }
            await Promise.all(p);

            if (!this.receiveSignalStream) {
                await this.createReceiveSignalStream();
            }
            return;
        }

        // Remove inactive connections
        const [activeConnections, inactiveConnections] = partition(
            this.connections,
            _ => _.isActive(),
        );
        await this.closeConnections(inactiveConnections);
        this.connections = activeConnections;

        this.gatherConnectionStats().catch((error: Error) => {
            logger.warn("Error calling gatherConnectionStats", { error });
        });
        this.statsLoopsExecuted++;

        // Update the server's knowledge of current local sessions
        const { localPeer } = this;
        const sessionIds = this.connections
            .filter(_ => _.getSessionId)
            .map(_ => defined(_.getSessionId()));
        localPeer.setSessionIdsList(sessionIds);

        const request = new RefreshPeerRequest();
        request.setPeer(localPeer);

        try {
            await DeadlineEnforcer.withDeadline(
                signalingClient.refreshPeer(request, await this.getMetadata()),
                this.grpcCallDeadline,
            );
        } catch (error) {
            if (
                error instanceof grpcWeb.RpcError &&
                error.code === grpcWeb.StatusCode.NOT_FOUND
            ) {
                logger.warn("Peer expired, creating new peer", {
                    peerId: localPeer.getPeerId(),
                });
                // If the peer is expired, we reset the entire RTC client,
                // including its connections.
                // In all other circumstances (e.g. the signaling API is inaccessible),
                // we keep the connections.
                await this.reset();
            } else {
                logger.warn("Error calling RefreshPeer", { error });
            }
        }

        const peers = await this.getPeers();
        for (const connection of this.connections) {
            connection.setRemoteDeviceId(
                peers.find(_ => _.id === connection.getRemotePeerId())
                    ?.deviceId,
            );
        }
    }

    private async gatherConnectionStats() {
        // throttling
        if (this.statsLoopsExecuted % 10 !== 0) {
            return;
        }

        // only concerned with single connection sessions
        if (this.connections.length !== 1) {
            return;
        }

        const connectionStatsInfo =
            await this.connections[0].getConnectionStatsInfo();
        const iceMode = connectionStatsInfo
            ? iceModeFromRtcConnectionStatsInfo(connectionStatsInfo)
            : undefined;

        if (iceMode) {
            stats.setTag("rtc-connection-type", iceMode);
        } else {
            stats.setTag("rtc-connection-type", "unknown");
        }
    }

    private async reset() {
        // this method is only called synchronously with the update loop,
        // so we don't have to worry about this.connections state mutating
        // due to reset in other methods, or in reset due to other methods
        await this.closeConnections(this.connections);
        this.connections = [];
        this.isOutgoingConnection.clear();
        await this.createPeer();
        await this.createReceiveSignalStream();
    }

    private async updateIceServers() {
        if (this.config.lanOnlyMode) {
            return;
        }

        const { iceServersLastUpdate, iceServersTtl } = this;
        const now = new Date().getTime();
        if (
            !iceServersLastUpdate ||
            now - iceServersLastUpdate > iceServersTtl
        ) {
            try {
                const response = await DeadlineEnforcer.withDeadline(
                    this.config.signalingClient.getIceServers(
                        new GetIceServersRequest(),
                        await this.getMetadata(),
                    ),
                    this.grpcCallDeadline,
                );
                const iceServers = response.getIceServers();
                this.iceServers = JSON.parse(iceServers);
                this.iceServersLastUpdate = now;
            } catch (error) {
                logger.warn("Error in updateIceServers", { error });
            }
        }
    }

    private setupHandlers(connection: RtcConnection) {
        const { peerConnection } = connection;
        const sessionCreatedTimestamp = connection.getSessionCreatedTimestamp();

        peerConnection.onconnectionstatechange = async () => {
            const connectionState = peerConnection.connectionState;

            if (!sessionCreatedTimestamp) {
                return;
            }

            const deviceId = connection.getRemoteDeviceId();

            // update rtc kpi metrics
            switch (connectionState) {
                case "connected":
                    stats.aggregate(
                        "rtc_connect_time",
                        new Date().getTime() - sessionCreatedTimestamp,
                        {
                            ...(deviceId ? { deviceId } : {}),
                        },
                    );
                    break;
                case "failed":
                    stats.increment("rtc_connect_failed", {
                        ...(deviceId ? { deviceId } : {}),
                    });
                    break;
                default:
                    break;
            }
        };
    }

    private async getMetadata(
        options: {
            hasDeadline?: false;
        } = {},
    ): Promise<grpcWeb.Metadata> {
        if (this.config.lanOnlyMode) {
            throw new Error(
                "getMetadata method cannot be called in local-only mode",
            );
        }

        const { getToken } = this.config;
        const { grpcCallDeadline } = this;
        const { hasDeadline = true } = options;

        const deadline = hasDeadline
            ? { deadline: (Date.now() + grpcCallDeadline).toString(10) }
            : null;

        try {
            const authorization = await getToken();
            return {
                authorization,
                ...deadline,
            };
        } catch (error) {
            logger.error("getToken() failed", { error });
            throw new Error("Cannot get authorization token");
        }
    }
}
